import { DotTargets, Point } from "./interfaces";

const TWO_PI = 2 * Math.PI;

export const normalizeSigned = (angle: number) => {
    const normalized = (angle + Math.PI) % TWO_PI;
    const wrapped = normalized < 0 ? normalized + TWO_PI : normalized;

    return wrapped - Math.PI;
};

export const angleToPoint = (
    angle: number,
    cx: number,
    cy: number,
    rad: number,
) => ({
    x: cx + Math.cos(angle) * rad,
    y: cy + Math.sin(angle) * rad,
});

export const pointToAngle = (point: Point, cx: number, cy: number) =>
    Math.atan2(point.y - cy, point.x - cx);

export const buildCirclePoints = (
    count: number,
    cx: number,
    cy: number,
    rad: number,
) => {
    const points: Point[] = [];

    for (let i = 0; i < count; ++i) {
        const theta = -Math.PI / 2 + (i / count) * TWO_PI;

        points.push({
            x: cx + Math.cos(theta) * rad,
            y: cy + Math.sin(theta) * rad,
        });
    }

    return points;
};

export const samplePathPoints = (d: string, count: number) => {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

    path.setAttribute("d", d);

    const totalLength = path.getTotalLength();
    const points: Point[] = [];

    for (let i = 0; i < count; ++i) {
        const point = path.getPointAtLength((i / count) * totalLength);

        points.push({ x: point.x, y: point.y });
    }

    return points;
};

const distanceSquared = (a: Point, b: Point) => {
    const dx = a.x - b.x;
    const dy = a.y - b.y;

    return dx * dx + dy * dy;
};

const shiftPoints = (points: Point[], shift: number) => {
    const n = points.length;

    if (n === 0) {
        return points;
    }

    const normalizedShift = ((shift % n) + n) % n;

    return [
        ...points.slice(normalizedShift),
        ...points.slice(0, normalizedShift),
    ];
};

const reversePoints = (points: Point[]) => {
    if (points.length === 0) {
        return points;
    }

    const [first, ...rest] = points;

    return [first, ...rest.reverse()];
};

const pointSetDistance = (a: Point[], b: Point[]) => {
    let sum = 0;

    for (let i = 0; i < a.length; ++i) {
        const dx = a[i].x - b[i].x;
        const dy = a[i].y - b[i].y;

        sum += dx * dx + dy * dy;
    }

    return sum;
};

export const alignPoints = (reference: Point[], candidate: Point[]) => {
    if (reference.length === 0 || candidate.length === 0) {
        return candidate;
    }

    const tryAlign = (base: Point[]) => {
        let best = base;
        let bestScore = Number.POSITIVE_INFINITY;

        for (let shift = 0; shift < base.length; ++shift) {
            const shifted = shiftPoints(base, shift);
            const score = pointSetDistance(reference, shifted);

            if (score < bestScore) {
                bestScore = score;
                best = shifted;
            }
        }

        return { points: best, score: bestScore };
    };

    const forward = tryAlign(candidate);
    const reversed = tryAlign(reversePoints(candidate));

    return forward.score <= reversed.score ? forward.points : reversed.points;
};

export const pointsToPath = (points: Point[], fallbackPath: string) => {
    if (points.length === 0) {
        return fallbackPath;
    }

    if (points.length < 3) {
        const first = points[0];
        const rest = points
            .slice(1)
            .map((point) => `L ${point.x} ${point.y}`)
            .join(" ");

        return `M ${first.x} ${first.y} ${rest} Z`;
    }

    const n = points.length;
    let path = `M ${points[0].x} ${points[0].y}`;

    for (let i = 0; i < n; ++i) {
        const p0 = points[(i - 1 + n) % n];
        const p1 = points[i];
        const p2 = points[(i + 1) % n];
        const p3 = points[(i + 2) % n];

        const c1x = p1.x + (p2.x - p0.x) / 6;
        const c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6;
        const c2y = p2.y - (p3.y - p1.y) / 6;

        path += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
    }

    return `${path} Z`;
};

export const interpolatePointSets = (
    fromPoints: Point[],
    toPoints: Point[],
    t: number,
) =>
    fromPoints.map((fromPoint, index) => {
        const toPoint = toPoints[index];

        return {
            x: fromPoint.x + (toPoint.x - fromPoint.x) * t,
            y: fromPoint.y + (toPoint.y - fromPoint.y) * t,
        };
    });

export const assignClosestTargets = (
    dotPositions: Point[],
    targets: Point[],
    cx: number,
    cy: number,
    dotAngles?: number[],
    requireForward = false,
): DotTargets => {
    let bestScore = Number.POSITIVE_INFINITY;
    let bestOrder = [0, 1, 2, 3];
    const order = [0, 1, 2, 3];
    const targetAngles = targets.map((p) => pointToAngle(p, cx, cy));

    const permute = (start: number) => {
        if (start === order.length) {
            let score = 0;

            for (let i = 0; i < dotPositions.length; ++i) {
                if (requireForward && dotAngles) {
                    const travel = normalizeSigned(
                        targetAngles[order[i]] - dotAngles[i],
                    );

                    if (travel < -1e-4) {
                        return;
                    }
                }

                score += distanceSquared(dotPositions[i], targets[order[i]]);
            }

            if (score < bestScore) {
                bestScore = score;
                bestOrder = [...order];
            }

            return;
        }

        for (let i = start; i < order.length; ++i) {
            [order[start], order[i]] = [order[i], order[start]];
            permute(start + 1);
            [order[start], order[i]] = [order[i], order[start]];
        }
    };

    permute(0);

    if (!Number.isFinite(bestScore) && requireForward) {
        return assignClosestTargets(dotPositions, targets, cx, cy);
    }

    return {
        dot1: targets[bestOrder[0]],
        dot2: targets[bestOrder[1]],
        dot3: targets[bestOrder[2]],
        dot4: targets[bestOrder[3]],
    };
};

export const circlePath = (cx: number, cy: number, r: number) =>
    `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${r * 2} 0 a ${r} ${r} 0 1 0 ${-r * 2} 0`;
